/**
 * Locks. A lock guards a door or a container. It is generated ONCE at map build
 * ("at conception") with a STATIC, seeded pin/tumbler layout — reproducible from
 * its tile via `mulberry32(hashSeed(mapSeed, x, y))` — so the (deferred) pick/
 * rake minigame reads a fixed puzzle. In THIS build nothing consumes the pins;
 * the lock is instead a first-class TARGET: the player can focus-lock it and
 * shoot/smash it, and its HP scales with `securityLevel`.
 *
 * A lock satisfies `Targetable` (sim/targetable.ts) so it slots into the focus/
 * perception system alongside guards without a humanoid body. Headless.
 */

import { CONFIG } from '../config';
import { hashSeed, mulberry32 } from './rng';
import type { Targetable } from './targetable';

export type LockOwner = 'door' | 'container';

/** One pin column of the tumbler. STATIC from conception (for the minigame):
 *  `height` = number of pin positions (3..6); `target` = the position the pin
 *  must be set to (the unlock pattern). */
export interface PinColumn {
  height: number;
  target: number;
}

export interface Lock extends Targetable {
  owner: LockOwner;
  /** id of the door/container this lock guards. */
  ownerId: number;
  /** 1..maxSecurityLevel — drives HP (how many rounds to shoot it off). */
  securityLevel: number;
  hp: number;
  hpMax: number;
  /** Still engaged. Destroying the lock (hp≤0) or the matching key clears it. */
  locked: boolean;
  /** 3..9 columns of pins — the static puzzle for the deferred minigame. */
  columns: PinColumn[];
  /** Matching key item id, or null (no key exists). */
  keyId: number | null;
  /** 'electronic' is a stub for the future cybersecurity/access-panel minigame. */
  kind: 'normal' | 'electronic';
}

/** Lock ids live in a high range so they never collide with world entity ids
 *  (players/guards/janitors, which start at 1). */
let nextLockId = 1_000_000;

export interface CreateLockArgs {
  owner: LockOwner;
  ownerId: number;
  x: number;
  y: number;
  securityLevel: number;
  keyId?: number | null;
  kind?: 'normal' | 'electronic';
}

/** Build a lock with a static, seeded pin layout + `securityLevel`-derived HP. */
export function createLock(args: CreateLockArgs): Lock {
  const cfg = CONFIG.lock;
  const level = Math.max(1, Math.min(cfg.maxSecurityLevel, args.securityLevel));
  const rng = mulberry32(hashSeed(cfg.mapSeed, args.x, args.y));
  // 3..9 columns, each with 3..6 pins and a random unlock target — fixed forever.
  const columnCount = 3 + Math.floor(rng() * 7);
  const columns: PinColumn[] = [];
  for (let i = 0; i < columnCount; i++) {
    const height = 3 + Math.floor(rng() * 4); // 3..6
    columns.push({ height, target: Math.floor(rng() * height) });
  }
  const hp = level * cfg.hpPerLevel;
  return {
    id: nextLockId++,
    x: args.x,
    y: args.y,
    body: { dead: false },
    perceivedByPlayer: false,
    hiddenByCover: false,
    marked: false,
    everSeenByPlayer: false,
    stance: 'walk',
    owner: args.owner,
    ownerId: args.ownerId,
    securityLevel: level,
    hp,
    hpMax: hp,
    locked: true,
    columns,
    keyId: args.keyId ?? null,
    kind: args.kind ?? 'normal',
  };
}

/** Apply `amount` damage to a lock; returns true if this hit destroyed it (the
 *  caller then unlocks the owner). Flat HP pool — never routes through the
 *  humanoid `applyDamage`. */
export function damageLock(lock: Lock, amount: number): boolean {
  if (!lock.locked || lock.body.dead) return false;
  lock.hp -= amount;
  if (lock.hp <= 0) {
    lock.hp = 0;
    lock.locked = false;
    lock.body.dead = true; // "destroyed" — drops out of the focusable set
    return true;
  }
  return false;
}

/** Unlock a lock without destroying it (used by a matching key). */
export function unlockWithKey(lock: Lock): void {
  lock.locked = false;
}

/** Re-engage an INTACT lock — "put the lock back on". No key needed (you just
 *  push it shut); only a destroyed lock (shot off) can't be re-locked. */
export function reLock(lock: Lock): void {
  if (!lock.body.dead) lock.locked = true;
}

/** Preset "lock budgets" (total pin count) offered by the dev randomizer — a
 *  fixed, select-only list (no free-typed values). The smallest (9 = 3 columns ×
 *  3 pins) is fully rakeable; larger budgets grow taller, more numerous pins. */
export const LOCK_BUDGET_PRESETS = [9, 12, 15, 18, 24, 30, 36, 45, 54] as const;

/** Build a random pin layout that spends `budget` total pins across 3..9 columns
 *  of 3..6 pins each. Used by the dev randomizer to make locks of a chosen size. */
export function columnsForBudget(budget: number, rng: () => number): PinColumn[] {
  const b = Math.max(9, Math.min(54, Math.round(budget)));
  // A column-count range that keeps every column inside 3..6 pins for this budget.
  const minCols = Math.max(3, Math.ceil(b / 6));
  const maxCols = Math.min(9, Math.floor(b / 3));
  const count = minCols >= maxCols ? minCols : minCols + Math.floor(rng() * (maxCols - minCols + 1));
  const heights = new Array<number>(count).fill(3);
  let extra = b - 3 * count; // pins still to place; each column may grow to 6
  let guard = 0;
  while (extra > 0 && guard++ < 10_000) {
    const i = Math.floor(rng() * count);
    const cur = heights[i]!;
    if (cur < 6) { heights[i] = cur + 1; extra--; }
    else if (heights.every((h) => h >= 6)) break;
  }
  return heights.map((h) => ({ height: h, target: Math.floor(rng() * h) }));
}

/** Randomize a lock IN PLACE with a fresh pin puzzle of the given budget, re-lock
 *  it, and restore its HP. Dev-only (the randomizer button). */
export function regenerateLock(lock: Lock, budget: number, rng: () => number): void {
  lock.columns = columnsForBudget(budget, rng);
  lock.locked = true;
  lock.body.dead = false;
  lock.hp = lock.hpMax;
}
